/**
 * R12.1-FE: Floating clock button + dialog. GPS via Capacitor with web fallback.
 * RBAC: OPERATORE sees only assigned worksites; Admin/Resp see all.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { attendanceApi } from '../../api/attendance';
import { worksitesApi } from '../../api/worksites';
import type { WorksiteListItem } from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from '../../context/SnackbarContext';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { getCurrentPosition, GeolocationError } from '../../platform/geolocation';
import { isNative } from '../../platform/isNative';
import axios from 'axios';

export function ClockWidget() {
  const { user } = useAuth();
  const { showMessage } = useSnackbar();
  const isOnline = useNetworkStatus();
  const [open, setOpen] = useState(false);
  const [clockedIn, setClockedIn] = useState<boolean | null>(null);
  const [worksites, setWorksites] = useState<WorksiteListItem[]>([]);
  const [worksiteId, setWorksiteId] = useState<string>('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingWorksites, setLoadingWorksites] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ valid: boolean; distanceMeters: number } | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const status = await attendanceApi.getStatus();
      setClockedIn(status.lastEventType === 'CLOCK_IN');
    } catch {
      setClockedIn(false);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadWorksites = useCallback(async () => {
    if (!user?.userId) return;
    setLoadingWorksites(true);
    try {
      const isOperatore = user.role === 'OPERATORE';
      const res = await worksitesApi.list({
        assigneeUserId: isOperatore ? user.userId : undefined,
        size: 500,
        status: 'IN_PROGRESS', // only active worksites for clock
      });
      setWorksites(res.content);
      if (res.content.length > 0 && !worksiteId) setWorksiteId(res.content[0].id);
    } catch {
      showMessage('Errore caricamento commesse.', 'error');
    } finally {
      setLoadingWorksites(false);
    }
  }, [user?.userId, user?.role, showMessage]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (open) {
      loadWorksites();
      loadStatus();
      setLastResult(null);
      setGpsError(null);
    }
  }, [open, loadWorksites, loadStatus]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setLastResult(null);
    setGpsError(null);
  };

  const handleClock = async () => {
    if (!worksiteId || !user) return;
    if (!isOnline) {
      showMessage('Nessuna connessione. Attiva i dati o il Wi‑Fi per timbrare.', 'error');
      return;
    }
    setSubmitting(true);
    setLastResult(null);
    setGpsError(null);
    try {
      const position = await getCurrentPosition();
      const dto = {
        worksiteId,
        latitude: position.latitude,
        longitude: position.longitude,
        accuracyMeters: position.accuracyMeters ?? undefined,
      };
      const isIn = clockedIn === false;
      const res = isIn
        ? await attendanceApi.clockIn(dto)
        : await attendanceApi.clockOut(dto);
      setLastResult({ valid: res.valid, distanceMeters: res.distanceFromWorksiteMeters });
      setClockedIn(!isIn);
      if (res.valid) {
        showMessage(isIn ? 'Ingresso timbrato.' : 'Uscita timbrata.', 'success');
        setTimeout(handleClose, 1500);
      }
    } catch (err) {
      if (err instanceof GeolocationError) {
        setGpsError(err.message);
        showMessage(err.message, 'error');
        return;
      }
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const msg = (err.response?.data as { message?: string })?.message ?? err.message;
        if (msg.includes('already CLOCK_IN') || msg.includes('clock out first')) {
          setClockedIn(true);
          showMessage('Sei già in ingresso. Timbra uscita quando termini.', 'info');
        } else if (msg.includes('no active clock-in')) {
          setClockedIn(false);
          showMessage('Nessun ingresso attivo. Timbra prima ingresso.', 'warning');
        } else if (msg.includes('assigned worksites')) {
          showMessage('Puoi timbrare solo per commesse a cui sei assegnato.', 'error');
        } else {
          showMessage(msg || 'Errore timbratura.', 'error');
        }
        return;
      }
      showMessage(
        err && typeof err === 'object' && 'message' in err
          ? String((err as Error).message)
          : 'Errore durante la timbratura.',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isClockedIn = clockedIn === true;
  const buttonLabel = isClockedIn ? 'Timbra Uscita' : 'Timbra Ingresso';
  const ButtonIcon = isClockedIn ? LogoutIcon : LoginIcon;

  return (
    <>
      <Fab
        color="primary"
        aria-label={buttonLabel}
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.speedDial }}
      >
        <ButtonIcon />
      </Fab>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Timbratura
          {!isNative() && (
            <Typography variant="caption" display="block" color="text.secondary">
              Su browser la posizione potrebbe essere meno precisa. Per timbrature affidabili usa l’app.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingStatus && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isOnline && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nessuna connessione. Impossibile timbrare.
            </Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }} disabled={loadingWorksites}>
            <InputLabel>Commessa</InputLabel>
            <Select
              value={worksiteId}
              label="Commessa"
              onChange={(e) => setWorksiteId(e.target.value)}
            >
              {worksites.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.title}
                </MenuItem>
              ))}
              {!loadingWorksites && worksites.length === 0 && (
                <MenuItem disabled>Nessuna commessa disponibile</MenuItem>
              )}
            </Select>
          </FormControl>
          {gpsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGpsError(null)}>
              {gpsError}
            </Alert>
          )}
          {lastResult !== null && (
            <Alert
              severity={lastResult.valid ? 'success' : 'error'}
              sx={{ mb: 2 }}
              onClose={() => setLastResult(null)}
            >
              {lastResult.valid ? (
                'Timbratura registrata correttamente.'
              ) : (
                <>
                  Fuori dal raggio consentito. Distanza dalla commessa:{' '}
                  <strong>{lastResult.distanceMeters} m</strong>. La timbratura è stata registrata
                  ma potrebbe essere considerata non valida.
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleClock}
            disabled={submitting || !worksiteId || !isOnline || loadingWorksites}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ButtonIcon />}
          >
            {submitting ? 'In corso...' : buttonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
